import { useState } from 'react';
import { MissingResourceError, ValidationError } from '#api/errors.ts';
import { useSuspenseRouteName } from '#api/routes.queries.ts';
import { Code } from '#components/atoms/code/index.ts';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { ConfirmDialog } from '#components/patterns/confirmations/confirm-dialog/ConfirmDialog.tsx';
import { useDeleteRoute } from './DeleteRouteDialog.queries.ts';

type Props = {
  routeId: `route-${string}`;
  onCancel: () => void;
  onDelete: () => void;
};

export function DeleteRouteDialog({ routeId, onCancel, onDelete }: Props) {
  const [validationErrors, setValidationErrors] = useState<string[] | null>(null);
  const deleteRoute = useDeleteRoute();
  const routeName = useSuspenseRouteName(routeId);

  // Clears out the validation error message, in case the modal is re-opened.
  function closeModal() {
    setValidationErrors(null);
    onCancel();
  }

  return (
    <ConfirmDialog
      title={<div className="truncate">Delete {routeName}?</div>}
      disableAccept={deleteRoute.isPending}
      acceptButtonText="Delete"
      onCancel={closeModal}
      onAccept={async () => {
        setValidationErrors(null);

        try {
          await deleteRoute.mutateAsync(routeId);
          onDelete();
        } catch (error: unknown) {
          if (error instanceof ValidationError) {
            setValidationErrors(error.errors.map((e) => e.message));
          } else if (error instanceof MissingResourceError) {
            setValidationErrors(['Route not found, maybe it was deleted already?']);
          } else {
            setValidationErrors(['There was a problem deleting the route.  Please try again or contact support.']);
          }
        }
      }}
      shouldRestoreFocusViaFocusScope={true}
    >
      <p className="p mb-0">
        Are you sure you want to delete <Code className="ml-6 wrap-anywhere">{routeName}</Code>?
      </p>
      {Boolean(validationErrors?.length) &&
        validationErrors.map((message, index) => (
          // eslint-disable-next-line react/no-array-index-key -- It's fine, these aren't going to move/change
          <ErrorMessage key={index} className={index === 0 ? 'mt-32' : undefined}>
            {message}
          </ErrorMessage>
        ))}
    </ConfirmDialog>
  );
}
